import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../hooks/useSearch';
import SearchResults from './SearchResults';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const modalRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const {
    query,
    results,
    isLoading,
    recentSearches,
    suggestions,
    hasResults,
    hasQuery,
    updateQuery,
    executeSearch,
    clearSearch,
    clearRecentSearches,
    getPopularSuggestions
  } = useSearch();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearSearch();
      setSelectedIndex(-1);
    }
  }, [isOpen, clearSearch]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => 
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => prev > -1 ? prev - 1 : prev);
          break;
        
        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0 && results[selectedIndex]) {
            handleResultClick(results[selectedIndex]);
          } else if (hasQuery) {
            executeSearch();
          }
          break;
        
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, hasQuery, executeSearch]);

  // Handle clicking outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleInputChange = (event) => {
    const newQuery = event.target.value;
    updateQuery(newQuery);
    setSelectedIndex(-1);
  };

  const handleResultClick = (result) => {
    if (result.route) {
      navigate(result.route);
      executeSearch(query);
      onClose();
    } else if (result.query) {
      updateQuery(result.query);
      setSelectedIndex(-1);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    updateQuery(suggestion);
    setSelectedIndex(-1);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleClearSearch = () => {
    clearSearch();
    setSelectedIndex(-1);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  if (!isOpen) return null;

  const popularSuggestions = getPopularSuggestions();
  const shouldShowRecent = !hasQuery && recentSearches.length > 0;
  const shouldShowPopular = !hasQuery && !shouldShowRecent;
  const shouldShowSuggestions = hasQuery && !hasResults && suggestions.length > 0;

  return (
    <div className="search-modal-overlay" role="dialog" aria-modal="true" aria-label="Search">
      <div className="search-modal" ref={modalRef}>
        <div className="search-modal-header">
          <div className="search-input-container">
            <div className="search-input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="Search pages, services, and content..."
              value={query}
              onChange={handleInputChange}
              autoComplete="off"
              spellCheck="false"
            />
            
            {(hasQuery || isLoading) && (
              <div className="search-input-actions">
                {isLoading && (
                  <div className="search-loading">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.49 8.49l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.49-8.49l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                )}
                
                {hasQuery && (
                  <button 
                    className="search-clear-btn"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          
          <button 
            className="search-close-btn"
            onClick={onClose}
            title="Close search"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="search-modal-body">
          {hasQuery && hasResults && (
            <SearchResults
              results={results}
              query={query}
              selectedIndex={selectedIndex}
              onResultClick={handleResultClick}
              onClose={onClose}
            />
          )}
          
          {hasQuery && !hasResults && !isLoading && (
            <SearchResults
              results={[]}
              query={query}
              onResultClick={handleResultClick}
              onClose={onClose}
            />
          )}
          
          {shouldShowSuggestions && (
            <div className="search-suggestions">
              <h3 className="search-section-title">Did you mean?</h3>
              <div className="search-suggestion-list">
                {suggestions.map(suggestion => (
                  <button
                    key={suggestion}
                    className="search-suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                      <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {shouldShowRecent && (
            <div className="search-recent">
              <div className="search-section-header">
                <h3 className="search-section-title">Recent searches</h3>
                <button 
                  className="search-clear-recent"
                  onClick={clearRecentSearches}
                  title="Clear recent searches"
                >
                  Clear
                </button>
              </div>
              <div className="search-recent-list">
                {recentSearches.map(recent => (
                  <button
                    key={recent}
                    className="search-recent-item"
                    onClick={() => handleSuggestionClick(recent)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {recent}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {shouldShowPopular && (
            <div className="search-popular">
              <h3 className="search-section-title">Popular searches</h3>
              <div className="search-popular-list">
                {popularSuggestions.map(popular => (
                  <button
                    key={popular}
                    className="search-popular-item"
                    onClick={() => handleSuggestionClick(popular)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="currentColor" strokeWidth="2" fill="none"/>
                    </svg>
                    {popular}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="search-modal-footer">
          <div className="search-shortcuts">
            <div className="search-shortcuts-main">
              <span className="search-shortcut">
                <kbd>↑</kbd><kbd>↓</kbd> Navigate
              </span>
              <span className="search-shortcut">
                <kbd>Enter</kbd> Select
              </span>
              <span className="search-shortcut">
                <kbd>Esc</kbd> Close
              </span>
            </div>
            <div className="search-shortcuts-tip">
              <span className="search-pro-tip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Pro tip: Use <kbd>{navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}</kbd><kbd>K</kbd> to quickly access search from anywhere
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;